import { Button, Flex, Heading1, TextSmall } from '@teamleader/ahoy';
import React, { useCallback } from 'react';
import BackToLoginLink from './BackToLoginLink';
import { useMultiFactorAuthenticationContext } from './MultiFactorAuthenticationContext';
import useMultiFactorAuthenticationActivate from './useMultifactorAuthenticationActivate';
import redirectToApplication from '../../../Infrastructure/Routing/redirectToApplication';
import UrlGenerator from '../../../Infrastructure/Routing/UrlGenerator';
import styles from './styles.css';
import { translate, Translation } from '@teamleader/i18n';
import {
  isEmailMultiFactorAuthenticationProviderOption,
  MultiFactorAuthenticationType,
} from '../../../Infrastructure/API/MultiFactorAuthentication/list';

interface EmailActivationProps {
  onGoBackToLoginClick: () => void;
}

const EmailActivation = ({ onGoBackToLoginClick }: EmailActivationProps) => {
  const { authToken, providerOptions } = useMultiFactorAuthenticationContext();
  const emailProviderOption = providerOptions.find(isEmailMultiFactorAuthenticationProviderOption);
  const {
    activate,
    fetched: activated,
    error: activationError,
    loading: activating,
  } = useMultiFactorAuthenticationActivate({ authToken, type: MultiFactorAuthenticationType.Email });

  const handleActivate = useCallback(() => {
    activate();
  }, [activate]);

  if (activated) {
    const appUrl = UrlGenerator().appRoot();
    redirectToApplication(appUrl);
  }

  return (
    <Flex direction="column" alignItems="stretch" gap={5}>
      <Flex direction="column" alignItems="flex-start" gap={5}>
        <BackToLoginLink className={styles.back} onClick={onGoBackToLoginClick} />
        <Heading1>{translate('mfa.emailActivation.title')}</Heading1>
      </Flex>
      <TextSmall>
        <Translation
          id="mfa.emailActivation.intro"
          values={{
            email: emailProviderOption?.email ?? '',
            strong: (chunks: string[]): JSX.Element => <strong>{chunks}</strong>,
          }}
        />
      </TextSmall>
      {activationError && (
        <TextSmall color="ruby" tint="dark">
          {translate('mfa.emailActivation.activateError')}
        </TextSmall>
      )}
      <Button size="large" level="primary" fullWidth type="button" processing={activating} onClick={handleActivate}>
        {translate('mfa.emailActivation.finishSetup')}
      </Button>
    </Flex>
  );
};

export default EmailActivation;
